import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { InputComponent } from 'src/app/components/shared/input';
import { CategoryService } from 'src/app/services/category';
import { LocaleService } from 'src/app/services/locale';
import { DropdownComponent } from '../../../../shared/dropdown';
import { TrashComponent } from '../../../../icons/trash';
import { PlusComponent } from '../../../../icons/plus';

@Component({
  selector: 'app-category1-question3',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    DropdownComponent,
    TrashComponent,
    PlusComponent
  ],
  template: `
    <div class="text-[13px]">
      {{ strings[language].question }}
    </div>
    <div class="text-[12px] text-[#616161] mt-1">
      {{ strings[language].answer }}
    </div>
    <br />
    <div class="flex flex-wrap gap-4 sm:gap-6">
      <div
        (click)="usage = true"
        class="flex gap-2 align-middle items-center cursor-pointer text-[13px]"
      >
        <div
          [ngClass]="{ 'bg-[#183A25]': usage }"
          class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
        ></div>
        <div>{{ strings[language].choice_yes }}</div>
      </div>
      <div>
        <div
          (click)="usage = false"
          class="flex gap-2 align-middle items-center cursor-pointer text-[13px]"
        >
          <div
            [ngClass]="{
              'bg-[#183A25]': !usage
            }"
            class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
          ></div>
          <div>{{ strings[language].choice_no }}</div>
        </div>
      </div>
    </div>
    <div *ngIf="usage">
      <div class="bg-[#043B22] flex px-3 py-2.5 rounded-t-lg gap-4 mt-6 items-center">
        <div class="text-white text-[13px] flex-1">
          {{ strings[language].label_vehicle_type }}
        </div>
        <div class="text-white text-[13px] flex-1">
          {{ strings[language].label_fuel_type }}
        </div>
        <div class="text-white text-[13px] flex-1">
          {{ strings[language].label_km_year }}
        </div>
        <div class="text-white text-[13px] flex-1">
          {{ strings[language].label_liters_year }}
        </div>
        <div
          (click)="createForm()"
          class="w-6 h-6 flex justify-center items-center cursor-pointer bg-white/20 hover:bg-white/30 rounded-full transition-colors"
        >
          <app-plus color="white"></app-plus>
        </div>
      </div>
      <div
        *ngFor="let vehicle of vehicleControls; let x = index"
        class="flex py-4 px-1 border-y border-[#DEE2E6] gap-5"
      >
        <div class="w-1/2">
          <app-dropdown
            placeholder="{{ strings[language].select }} {{
              strings[language].label_vehicle_type
            }}"
            [options]="[
              {
                value: 'car',
                label: this.strings[this.language].car
              },
              {
                value: 'motorcycle',
                label: this.strings[this.language].moto
              }
            ]"
            [control]="vehicle.row['vehicle']"
          ></app-dropdown>
        </div>
        <div class="w-1/2">
          <app-dropdown
            (change)="onKeyUp()"
            placeholder="{{ strings[language].select }} {{
              strings[language].label_fuel_type
            }}"
            [options]="[
              {
                value: 'diesel',
                label: this.strings[this.language].diesel
              },
              {
                value: 'petrol',
                label: this.strings[this.language].gas
              },
              {
                value: 'electric',
                label: this.strings[this.language].electric
              },
            ]"
            [control]="vehicle.row['fuel']"
          ></app-dropdown>
        </div>
        <div class="w-1/2">
          <app-input
            [index]="x"
            [keyUp]="onKeyUp"
            type="number"
            [dollar]="false"
            unit="Km"
            placeholder="0.00"
            [control]="vehicle.row['kilometers']"
          >
          </app-input>
        </div>
        <div class="w-1/2">
          <app-input
            [index]="x"
            [keyUp]="onKeyUp"
            type="number"
            [dollar]="false"
            unit="l"
            placeholder="0.00"
            [control]="vehicle.row['liters']"
          >
          </app-input>
        </div>
        <div
          (click)="vehicleControls.pop()"
          class="w-1/8 flex justify-end pr-2 align-middle items-center cursor-pointer"
        >
          <app-trash></app-trash>
        </div>
      </div>
    </div>
  `
})
export class Category1Question3Component {
  language = inject(LocaleService).getLanguage();
  categoryService = inject(CategoryService);
  categories = this.categoryService.categories();

  usage = false;

  vehicleControls = [
    {
      row: {
        vehicle: new FormControl('', Validators.required),
        fuel: new FormControl('', Validators.required),
        kilometers: new FormControl('', Validators.required),
        liters: new FormControl('', Validators.required)
      }
    }
  ];

  form = new FormGroup({
    rows: new FormControl(this.vehicleControls)
  });

  strings: any = {
    english: {
      question: 'Do you own company vehicles?',
      answer: 'Please fill in the required data if yes to proceed.',
      choice_yes: 'Yes',
      choice_no: 'No',
      label_vehicle_type: 'Vehicle type',
      label_fuel_type: 'Fuel type',
      label_km_year: 'Kilometers / yr',
      label_liters_year: 'Liters / yr',
      select: 'Select',
      car: 'Car',
      moto: 'Motorcycle',
      diesel: 'Diesel',
      gas: 'Gasoline',
      fuel: 'Fuel Oil',
      electric: 'Electric',
      hybrid: 'Hybrid'
    },
    italian: {
      question: 'Possiedi veicoli aziendali?',
      answer:
        'Si prega di inserire i dati richiesti se la risposta è sì per procedere.',
      choice_yes: 'Sì',
      choice_no: 'No',
      label_vehicle_type: 'Tipo di veicolo',
      label_fuel_type: 'Tipo di combustibile',
      label_km_year: 'Chilometri / anno',
      label_liters_year: 'Litri / anno',
      select: 'Seleziona',
      car: 'Automobile',
      moto: 'Motocicletta',
      diesel: 'Diesel',
      gas: 'Benzina',
      fuel: 'Olio combustibile',
      electric: 'Elettrico',
      hybrid: 'Ibrido'
    }
  };

  ngOnInit() {
    let categories = localStorage.getItem('categories');

    if (categories) {
      categories = JSON.parse(categories);

      // @ts-ignore
      const answers = categories[0].questions[2].answers;

      if (answers?.length > 0) {
        let newControls: any = [];

        // @ts-ignore
        answers?.map((ex: any) => {
          newControls.push({
            row: {
              vehicle: new FormControl(ex.vehicle, Validators.required),
              fuel: new FormControl(ex.fuel, Validators.required),
              kilometers: new FormControl(ex.kilometers, Validators.required),
              liters: new FormControl(ex.liters, Validators.required)
            }
          });
        });

        this.usage = true;

        this.vehicleControls = newControls;
        this.form.setValue({ rows: newControls });
      }
    }
  }

  createForm = () => {
    this.vehicleControls.push({
      row: {
        vehicle: new FormControl('', Validators.required),
        fuel: new FormControl('', Validators.required),
        kilometers: new FormControl('', Validators.required),
        liters: new FormControl('', Validators.required)
      }
    });
  };

  onKeyUp = () => {
    let newAnswers = this.form.value.rows?.map(row => {
      return {
        vehicle: row.row.vehicle.value,
        fuel: row.row.fuel.value,
        kilometers: row.row.kilometers.value,
        liters: row.row.liters.value
      };
    });

    // @ts-ignore
    this.categories[0].questions[2].answers = newAnswers;
    this.categoryService.persistCategories(this.categories);
  };
}
